"use client"

import { Activity, RefreshCw } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { describeSyncDelay, useDeviceCalibration } from "@/hooks/use-device-calibration"
import { useTranslations } from "next-intl"

interface DeviceCalibrateButtonProps {
  /** "default" uses the app's themed outline button; "dark" matches the black floating panels (Gooncave);
   * "icon" is a compact icon-only button for tight control rows (the booru script mini player). */
  appearance?: "default" | "dark" | "icon"
  /** Show the current sync delay as a line of text under the button. */
  showStatus?: boolean
  className?: string
}

/**
 * The same Auto Calibrate everywhere. It measures the connected Handy(s) and records their latency
 * (see use-device-calibration). Disabled, with an explanation, when there is nothing it can measure.
 */
export function DeviceCalibrateButton({ appearance = "default", showStatus = true, className }: DeviceCalibrateButtonProps) {
  const t = useTranslations("deviceCalibrateButton")
  const { calibrate, isCalibrating, canCalibrate, activeSyncDelay, activeLatency } = useDeviceCalibration()
  const disabled = isCalibrating || !canCalibrate
  const title = !canCalibrate
    ? "Connect a Handy to calibrate its sync delay"
    : "Measure this device's network latency. Adjust playback sync separately if needed"

  const status = !canCalibrate
    ? "Connect a Handy to calibrate"
    : activeLatency > 0 || activeSyncDelay !== 0
      ? `${describeSyncDelay(activeSyncDelay)}${activeLatency > 0 ? ` · measured ${activeLatency} ms` : ""}`
      : "Not calibrated yet"

  if (appearance === "icon") {
    return (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={cn("h-8 w-8 shrink-0", className)}
        onClick={() => void calibrate()}
        disabled={disabled}
        title={status ? `${title}. ${status}` : title}
        aria-label={t("autoCalibrate")}
      >
        {isCalibrating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
      </Button>
    )
  }

  const dark = appearance === "dark"
  return (
    <div className={cn("space-y-1", className)}>
      {dark ? (
        <button
          type="button"
          onClick={() => void calibrate()}
          disabled={disabled}
          title={title}
          className={cn(
            "flex h-8 w-full items-center justify-center gap-2 rounded-lg bg-white/10 text-xs font-medium text-white transition-colors hover:bg-white/20",
            disabled && "cursor-not-allowed opacity-40",
          )}
        >
          {isCalibrating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
          {isCalibrating ? "Calibrating..." : "Auto calibrate"}
        </button>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 w-full gap-1.5 text-xs"
          onClick={() => void calibrate()}
          disabled={disabled}
          title={title}
        >
          {isCalibrating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
          {isCalibrating ? "Calibrating..." : "Auto calibrate"}
        </Button>
      )}
      {showStatus && status && (
        <p className={cn("text-[11px] leading-tight", dark ? "text-white/50" : "text-muted-foreground")}>{status}</p>
      )}
    </div>
  )
}
